/*
 * BioIndex
 * Copyright (c) 2026 BioIndex™. All rights reserved.
 * Unauthorized copying, scraping, redistribution, or commercial reuse is prohibited.
 */

import { Link } from "react-router-dom";
import PolicyLanguageToggle, {
  usePolicyLanguage,
} from "../components/PolicyLanguageToggle";

const copy = {
  es: {
    title: "Condiciones de uso",
    date: "Vigentes desde el 30 de julio de 2026 · Versión 2026-07-30",
    introduction:
      "Estas Condiciones regulan tu acceso y uso del sitio web, la aplicación móvil, las funciones comunitarias y los servicios relacionados de BioIndex™. Revísalas antes de crear o publicar contenido.",
    returnHome: "Volver a BioIndex",
    rights: "Todos los derechos reservados.",
    sections: [
      {
        title: "Aceptación y responsable",
        text: (
          <>
            BioIndex™ es operado por Luis Carlos Beltrán Lacouture en Colombia.
            Estas Condiciones constituyen un acuerdo entre tú y BioIndex. Las
            aceptas al seleccionar la casilla de aceptación y continuar usando
            una cuenta. Las{" "}
            <Link
              to="/community-guidelines"
              className="font-medium text-[#01767A] underline"
            >
              Normas de la comunidad
            </Link>{" "}
            forman parte de estas Condiciones. La{" "}
            <Link
              to="/privacy"
              className="font-medium text-[#01767A] underline"
            >
              Política de privacidad
            </Link>{" "}
            explica cómo se trata la información personal.
          </>
        ),
      },
      {
        title: "Requisitos y cuentas",
        text: "Debes tener al menos 18 años para crear y utilizar una cuenta de BioIndex. Debes proporcionar información precisa, proteger el acceso a tu cuenta y notificar de inmediato a BioIndex si crees que tu cuenta ha sido comprometida. Eres responsable de toda la actividad realizada mediante tu cuenta.",
      },
      {
        title: "Propósito de BioIndex",
        text: "BioIndex es una plataforma de documentación, educación y comunidad sobre biodiversidad para registrar, organizar, explorar y compartir observaciones de la naturaleza. BioIndex puede proporcionar sugerencias taxonómicas e información educativa, pero cada usuario es responsable de revisar sus registros y actuar con el criterio adecuado en campo.",
      },
      {
        title: "Tu contenido y la licencia",
        text: "Conservas la propiedad de las fotografías, observaciones, notas, comentarios y demás contenido original que envíes. Otorgas a BioIndex una licencia no exclusiva, mundial y libre de regalías para alojar, almacenar, reproducir, adaptar el formato, mostrar y distribuir ese contenido únicamente cuando sea necesario para operar, proteger, mejorar y presentar BioIndex. Esta licencia termina cuando se elimina el contenido, salvo durante períodos razonables de respaldo, por obligaciones legales, registros de moderación y copias previamente compartidas o conservadas por otros usuarios.",
      },
      {
        title: "Permisos sobre el contenido enviado",
        text: "Solo puedes enviar contenido creado por ti o que tengas autorización para utilizar. Tu contenido no debe vulnerar derechos de autor, privacidad, imagen, contratos u otros derechos de otra persona. También debes contar con autorización antes de identificar a otra persona o publicar información personal sobre ella.",
      },
      {
        title: "Observaciones y ubicaciones públicas",
        text: "La información del perfil, fotografías, identificaciones taxonómicas, fechas y ubicaciones de observación, notas, actividad en foros, comentarios, reacciones y contribuciones comunitarias similares pueden ser visibles para otros usuarios. Eres responsable de revisar la información antes de publicarla. No reveles direcciones privadas ni ubicaciones exactas cuando hacerlo pueda poner en riesgo a una persona, área protegida, especie vulnerable, nido, madriguera, dormidero u otro recurso biológico sensible.",
      },
      {
        title: "Contenido y conductas prohibidas",
        text: "No puedes enviar contenido ilegal, amenazante, que promueva el odio, acosador, sexualmente explícito, explotador, fraudulento, difamatorio, invasivo o deliberadamente engañoso. Se prohíben la suplantación, el acoso dirigido, el spam, los enlaces maliciosos, la publicidad no autorizada, la manipulación de funciones comunitarias, las violaciones de privacidad, la fabricación intencional de registros de biodiversidad y los intentos por interrumpir o comprometer BioIndex.",
      },
      {
        title: "Moderación, denuncias y bloqueos",
        text: "Los usuarios pueden denunciar contenido o cuentas mediante los controles disponibles o la página de contacto de BioIndex. También pueden utilizar los controles de bloqueo disponibles. BioIndex puede investigar denuncias y eliminar contenido, limitar su visibilidad, conservar registros relevantes, restringir funciones, suspender cuentas o cerrarlas cuando sea razonablemente necesario para aplicar estas Condiciones, proteger a los usuarios o a la vida silvestre, cumplir la ley o preservar la integridad y seguridad de la plataforma.",
      },
      {
        title: "Propiedad intelectual de BioIndex",
        text: "El nombre BioIndex, su interfaz, código fuente, organización de bases de datos, textos originales, insignias, cuestionarios, ilustraciones, logotipos, íconos y demás recursos originales están protegidos por derechos de autor, marcas y otras leyes aplicables. Estas Condiciones no transfieren a los usuarios la propiedad intelectual de BioIndex.",
      },
      {
        title: "Extracción y acceso automatizado",
        text: "Sin autorización previa y escrita de BioIndex, no puedes extraer, rastrear, replicar, descargar masivamente, reproducir sistemáticamente, redistribuir, vender, explotar comercialmente ni utilizar contenido de BioIndex para crear o apoyar una base de datos competidora. También se prohíben la recopilación automatizada de datos, minería de datos, entrenamiento de modelos, generación de conjuntos de datos, pruebas de seguridad no autorizadas, evasión de medidas técnicas y los intentos por exceder los límites de acceso.",
      },
      {
        title: "Aviso sobre información científica",
        text: "Las identificaciones, nombres comunes, clasificaciones, mapas, estadísticas y demás información científica de BioIndex pueden estar incompletos, ser objeto de debate o contener errores. BioIndex es una plataforma educativa y de documentación, y no garantiza la exactitud científica de las contribuciones de los usuarios. La información de BioIndex debe verificarse de manera independiente antes de utilizarse en investigación, decisiones de conservación, salud, seguridad, cumplimiento legal o gestión ambiental.",
      },
      {
        title: "Eliminación y cierre de cuentas",
        text: "Puedes solicitar la eliminación de tu cuenta mediante los controles disponibles en tu Perfil o por medio de la página de contacto de BioIndex si no puedes acceder a ella. BioIndex puede suspender o cerrar una cuenta por infracciones graves o reiteradas, riesgos de seguridad, requisitos legales o uso indebido prolongado. Cierta información puede conservarse cuando sea razonablemente necesaria para seguridad, cumplimiento legal, resolución de disputas o aplicación de estas Condiciones.",
      },
      {
        title: "Servicios de terceros",
        text: "BioIndex depende de servicios de terceros, entre ellos la autenticación de Google, Firebase, servicios cartográficos, sistemas operativos de dispositivos y plataformas de distribución de aplicaciones. Estos servicios pueden estar sujetos a condiciones y políticas de privacidad independientes. BioIndex no es responsable de interrupciones o cambios causados exclusivamente por un proveedor externo.",
      },
      {
        title: "Cambios en estas Condiciones",
        text: "BioIndex puede modificar estas Condiciones a medida que evolucione la plataforma. La fecha de entrada en vigor y la versión aparecerán en esta página. Si un cambio afecta de manera sustancial las obligaciones de los usuarios o el tratamiento de su contenido, BioIndex puede exigir que revisen y acepten expresamente la versión actualizada antes de enviar contenido público adicional.",
      },
      {
        title: "Disponibilidad y garantías",
        text: "BioIndex se proporciona “tal cual” y “según disponibilidad”, en la máxima medida permitida por la ley aplicable. BioIndex no garantiza disponibilidad ininterrumpida, conservación permanente del contenido, ausencia de errores ni idoneidad para toda finalidad científica, educativa o personal. Nada de lo contenido en estas Condiciones excluye protecciones que legalmente no puedan excluirse.",
      },
      {
        title: "Legislación aplicable",
        text: "Estas Condiciones se rigen por las leyes de Colombia. Nada de lo dispuesto aquí limita los derechos obligatorios de protección al consumidor, privacidad, protección de datos u otros derechos legales que te correspondan. Toda disputa será presentada ante las autoridades o tribunales competentes determinados por la ley aplicable.",
      },
      {
        title: "Contacto",
        text: (
          <>
            Las preguntas, notificaciones legales, denuncias o solicitudes
            relacionadas con estas Condiciones pueden enviarse mediante la{" "}
            <Link
              to="/contact"
              className="font-medium text-[#01767A] underline"
            >
              página de contacto de BioIndex
            </Link>
            .
          </>
        ),
      },
    ],
  },
  en: {
    title: "Terms of Use",
    date: "Effective July 30, 2026 · Version 2026-07-30",
    introduction:
      "These Terms govern your access to and use of the BioIndex™ website, mobile application, community features, and related services. Please review them before creating or publishing content.",
    returnHome: "Return to BioIndex",
    rights: "All rights reserved.",
    sections: [
      {
        title: "Acceptance and operator",
        text: (
          <>
            BioIndex™ is operated by Luis Carlos Beltrán Lacouture in Colombia.
            These Terms form an agreement between you and BioIndex. You accept
            them when you select the agreement checkbox and continue using an
            account. The{" "}
            <Link
              to="/community-guidelines"
              className="font-medium text-[#01767A] underline"
            >
              Community Guidelines
            </Link>{" "}
            form part of these Terms. The{" "}
            <Link
              to="/privacy"
              className="font-medium text-[#01767A] underline"
            >
              Privacy Policy
            </Link>{" "}
            explains how personal information is handled.
          </>
        ),
      },
      {
        title: "Eligibility and accounts",
        text: "You must be at least 18 years old to create and use a BioIndex account. You must provide accurate account information, protect access to your account, and promptly notify BioIndex if you believe your account has been compromised. You are responsible for all activity performed through your account.",
      },
      {
        title: "Purpose of BioIndex",
        text: "BioIndex is a biodiversity documentation, education, and community platform for recording, organizing, exploring, and sharing observations of nature. BioIndex may provide taxonomic suggestions and educational information, but users remain responsible for reviewing their records and using appropriate judgment in the field.",
      },
      {
        title: "Your content and license",
        text: "You retain ownership of photographs, observations, notes, comments, and other original content you submit. You grant BioIndex a non-exclusive, worldwide, royalty-free license to host, store, reproduce, format, display, and distribute that content only as needed to operate, secure, improve, and present BioIndex. This license ends when the content is deleted, except for reasonable backup periods, legal obligations, moderation records, and copies previously shared or retained by other users.",
      },
      {
        title: "Permissions for submitted content",
        text: "You may submit only content that you created or have permission to use. Your content must not violate another person's copyright, privacy, publicity, contractual, or other rights. You must also have permission before identifying or publishing personal information about another person.",
      },
      {
        title: "Public observations and locations",
        text: "Profile information, photographs, taxonomic identifications, observation dates, locations, notes, forum activity, comments, reactions, and similar community contributions may be visible to other users. You are responsible for reviewing information before publishing it. Do not disclose private addresses or exact locations when doing so could endanger a person, protected area, vulnerable species, nest, den, roost, or other sensitive biological resource.",
      },
      {
        title: "Prohibited content and conduct",
        text: "You may not submit illegal, threatening, hateful, harassing, sexually explicit, exploitative, fraudulent, defamatory, invasive, or deliberately misleading content. Impersonation, targeted harassment, spam, malicious links, unauthorized advertising, manipulation of community features, privacy violations, intentional fabrication of biodiversity records, and attempts to disrupt or compromise BioIndex are prohibited.",
      },
      {
        title: "Moderation, reporting, and blocking",
        text: "Users may report content or accounts through available reporting controls or through the BioIndex contact page. Users may also use available blocking controls. BioIndex may investigate reports and remove content, limit visibility, preserve relevant records, restrict features, suspend accounts, or terminate accounts when reasonably necessary to enforce these Terms, protect users or wildlife, comply with law, or preserve the integrity and security of the platform.",
      },
      {
        title: "BioIndex intellectual property",
        text: "The BioIndex name, interface, source code, database organization, original text, badges, quizzes, illustrations, logos, icons, and other original assets are protected by copyright, trademark, and other applicable laws. These Terms do not transfer ownership of BioIndex intellectual property to users.",
      },
      {
        title: "Scraping and automated access",
        text: "Without BioIndex’s prior written permission, you may not scrape, crawl, mirror, bulk-download, systematically reproduce, redistribute, sell, commercially exploit, or use BioIndex content to create or support a competing database. Automated data collection, data mining, model training, dataset generation, unauthorized security testing, circumvention of technical safeguards, and attempts to exceed access limits are also prohibited.",
      },
      {
        title: "Scientific information disclaimer",
        text: "Identifications, common names, classifications, maps, statistics, and other scientific information on BioIndex may be incomplete, disputed, or incorrect. BioIndex is an educational and documentation platform and does not guarantee the scientific accuracy of user contributions. Information from BioIndex should be independently verified before it is used for research, conservation decisions, health, safety, legal compliance, or environmental management.",
      },
      {
        title: "Account deletion and termination",
        text: "You may request deletion of your account through the account controls in your Profile or through the BioIndex contact page if you cannot access the account. BioIndex may suspend or terminate an account for serious or repeated violations, security risks, legal requirements, or prolonged misuse. Some information may be retained when reasonably required for security, legal compliance, dispute resolution, or enforcement.",
      },
      {
        title: "Third-party services",
        text: "BioIndex depends on third-party services, including Google authentication, Firebase, mapping services, device operating systems, and app-distribution platforms. Their services may be subject to separate terms and privacy policies. BioIndex is not responsible for an interruption or change caused solely by a third-party provider.",
      },
      {
        title: "Changes to these Terms",
        text: "BioIndex may revise these Terms as the platform evolves. The effective date and version will be displayed on this page. If a change materially affects user obligations or the handling of user content, BioIndex may require users to review and affirmatively accept the updated version before they can submit additional public content.",
      },
      {
        title: "Availability and warranties",
        text: "BioIndex is provided “as is” and “as available,” to the fullest extent permitted by applicable law. BioIndex does not guarantee uninterrupted availability, permanent preservation of content, freedom from errors, or suitability for every scientific, educational, or personal purpose. Nothing in these Terms excludes protections that cannot legally be excluded.",
      },
      {
        title: "Governing law",
        text: "These Terms are governed by the laws of Colombia. Nothing in these Terms limits any mandatory consumer, privacy, data-protection, or other legal rights that apply to you. Any dispute will be submitted to the competent authorities or courts determined under applicable law.",
      },
      {
        title: "Contact",
        text: (
          <>
            Questions, legal notices, reports, or requests concerning these Terms
            may be submitted through the{" "}
            <Link
              to="/contact"
              className="font-medium text-[#01767A] underline"
            >
              BioIndex contact page
            </Link>
            .
          </>
        ),
      },
    ],
  },
};

export default function TermsPage() {
  const [language, setLanguage] = usePolicyLanguage();
  const content = copy[language];

  return (
    <main className="min-h-screen bg-[#374049] px-4 py-8 text-[#374049]">
      <section className="mx-auto max-w-4xl overflow-hidden rounded-3xl border border-[#93CA87]/40 bg-[#FCFFF6] shadow-lg">
        <div className="bg-[#374049] px-6 py-4">
          <img
            src="/BioIndex Prototype Banner.png"
            alt="BioIndex"
            className="mx-auto max-h-40 w-full max-w-md object-contain"
          />
        </div>

        <div className="p-6 sm:p-8">
          <div className="flex justify-end">
            <PolicyLanguageToggle
              language={language}
              onChange={setLanguage}
            />
          </div>

          <div className="mt-6 border-b border-[#374049]/10 pb-6">
            <h1
              className="text-4xl font-semibold"
              style={{ fontFamily: "Cormorant Garamond" }}
            >
              {content.title}
            </h1>

            <p className="mt-2 text-xs text-[#374049]/50">{content.date}</p>

            <p className="mt-5 max-w-3xl text-sm leading-7 text-[#374049]/75">
              {content.introduction}
            </p>
          </div>

          <div className="mt-6 grid gap-4 md:grid-cols-2">
            {content.sections.map((section, index) => (
              <article
                key={section.title}
                className="rounded-2xl border border-[#374049]/10 bg-white/70 p-5 shadow-sm"
              >
                <div className="mb-3 flex items-center gap-3">
                  <span className="flex h-8 w-8 shrink-0 items-center justify-center rounded-xl bg-[#24C3BE]/10 text-sm font-semibold text-[#24C3BE]">
                    {index + 1}
                  </span>
                  <h2 className="text-base font-semibold text-[#374049]">
                    {section.title}
                  </h2>
                </div>

                <div className="text-sm leading-7 text-[#374049]/70">
                  {section.text}
                </div>
              </article>
            ))}
          </div>

          <div className="mt-8 flex flex-col items-center border-t border-[#374049]/10 pt-7 text-center">
            <Link
              to="/"
              className="inline-flex items-center justify-center rounded-xl bg-[#374049] px-6 py-2.5 text-sm font-semibold text-white transition hover:bg-[#2f373e]"
            >
              {content.returnHome}
            </Link>

            <p className="mt-5 text-xs leading-6 text-[#374049]/55">
              © {new Date().getFullYear()} BioIndex™. {content.rights}
            </p>
          </div>
        </div>
      </section>
    </main>
  );
}
